import { humanize } from './format.js';
import { Badge } from './ui.js';

/**
 * An agent run's plan or review, as text: a numbered list when it has
 * `steps`, a verdict badge plus notes for a review, a small fact list for any
 * other flat object, and the JSON only as a last resort. Agent output can echo
 * third-party request text, so it never renders as markup or links. `bare`
 * drops the card frame for use inside another card (the approval it is being
 * decided on).
 */
export default function Structured({ value, bare = false }: { value: unknown; bare?: boolean }): JSX.Element {
  const v = value as { summary?: unknown; steps?: unknown; verdict?: unknown; notes?: unknown };
  const frame = bare ? 'lo-structured' : 'lo-card';
  const steps = Array.isArray(v?.steps) ? v.steps.map(stepText) : null;
  if (steps) {
    return (
      <div className={frame}>
        {typeof v.summary === 'string' && <p className="lo-card__body">{v.summary}</p>}
        <ol className="lo-steps">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      </div>
    );
  }
  if (typeof v?.verdict === 'string') {
    const notes = typeof v.notes === 'string' ? v.notes.trim() : '';
    return (
      <div className={frame}>
        <p className="lo-card__body lo-verdict">
          <Badge tone={v.verdict === 'pass' ? 'good' : v.verdict === 'warn' ? 'warn' : 'bad'}>{humanize(v.verdict)}</Badge>
          {notes && <span>{notes}</span>}
        </p>
      </div>
    );
  }
  const facts = flatFacts(value);
  if (facts) {
    return (
      <div className={frame}>
        <dl className="lo-facts">
          {facts.map(([k, val]) => (
            <FactRow key={k} k={k} v={val} />
          ))}
        </dl>
      </div>
    );
  }
  return <pre className="lo-pre">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>;
}

function FactRow({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <>
      <dt>{humanize(k)}</dt>
      <dd>{v}</dd>
    </>
  );
}

/** A plain object of strings, numbers and booleans as [key, text] pairs; null for anything nested. */
function flatFacts(value: unknown): [string, string][] | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value as Record<string, unknown>).filter(([, x]) => x !== null && x !== undefined && x !== '');
  if (entries.length === 0) return null;
  const out: [string, string][] = [];
  for (const [k, x] of entries) {
    if (typeof x === 'string' || typeof x === 'number') out.push([k, String(x)]);
    else if (typeof x === 'boolean') out.push([k, x ? 'Yes' : 'No']);
    else return null;
  }
  return out;
}

function stepText(s: unknown): string {
  if (typeof s === 'string') return s;
  if (typeof s === 'object' && s && 'title' in s) return String((s as { title: unknown }).title);
  return JSON.stringify(s);
}
